import React, { useState, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { filterData, formatValue } from '@ts-core/utils/dataTableUtils';

import TSTable from '@ts-core/components/Table/TSTable';
import { TSTableColumn } from '@ts-core/components/Table/TSTable/TSTable';

import getTimeString from '@ts-core/utils/getTimeString';

import { GeneralRankingStudent, RankingStats } from '../../types';

interface GeneralRankingTableProps {
  downloadFileName?: string;
  datesPicker?: React.ReactNode;
  studentsMode?: boolean;
  students?: GeneralRankingStudent[];
  stats?: RankingStats;
  error?: string;
  loading?: boolean;
  getGeneralRanking?: () => void;
}

const GeneralRankingTable = (props: GeneralRankingTableProps) => {
  const intl = useIntl();

  const { downloadFileName, datesPicker, studentsMode, getGeneralRanking, students = [], stats, loading = false, error: errorMessage } = props;

  const [query, setQuery] = useState('');

  const columns = useMemo((): TSTableColumn<GeneralRankingStudent>[] => {
    const result: TSTableColumn<GeneralRankingStudent>[] = [
      {
        Header: intl.formatMessage({ id: 'ranking.position' }),
        value: intl.formatMessage({ id: 'ranking.position' }),
        accessor: 'position',
        sortable: true,
        Footer: intl.formatMessage({ id: 'averages' }),
      },
      {
        Header: intl.formatMessage({ id: 'assignmentResultsTable.student' }),
        value: intl.formatMessage({ id: 'assignmentResultsTable.student' }),
        accessor: 'name',
        sortable: true,
      },
    ];
    if (students.some((s) => s.group)) {
      result.push({
        Header: intl.formatMessage({ id: 'studentsTable.column.group' }),
        value: intl.formatMessage({ id: 'studentsTable.column.group' }),
        accessor: 'group',
        id: 'group',
        sortable: true,
        filterable: true,
      });
    }

    if (students.some((s) => s.time)) {
      result.push({
        Header: intl.formatMessage({ id: 'time' }),
        value: intl.formatMessage({ id: 'time' }),
        accessor: 'time',
        sortable: true,
        valueTransformer: (time) => getTimeString(time * 60),
      });
    }

    result.push({
      Header: intl.formatMessage({ id: 'score' }),
      value: intl.formatMessage({ id: 'score' }),
      accessor: 'score',
      valueTransformer: (value) => formatValue(value),
      sortable: true,
      Footer: formatValue(stats?.average),
    });

    return result;
  }, [intl, stats, students]);

  const keys = ['name', 'group'];

  const filteredData = filterData(students, query, keys);

  return (
    <TSTable
      title={!studentsMode && datesPicker}
      data={filteredData}
      columns={columns}
      onSearch={(text) => setQuery(text)}
      allowDownloadAsXLSX={!studentsMode}
      loading={loading}
      errorMessage={errorMessage}
      xlsxFileName={downloadFileName}
      onRefreshData={getGeneralRanking}
    />
  );
};

export default GeneralRankingTable;
